import MuiSlider, { SliderProps as MuiSliderProps } from "@mui/material/Slider";
import React, { ChangeEvent, FunctionComponent } from "react";
import { GridRow } from "../grid/GridContainer";
import TextField, { TextFieldProps } from "../textfield/TextField";

export interface SliderProps extends Omit<MuiSliderProps, "max" | "min" | "onChange" | "value"> {
    /** includes a textField component to reflect the slider value */
    includeTextField?: boolean;

    /** max value for the slider and input */
    max: number;

    /** min value for the slider and input */
    min: number;

    /** onChange function that will sync the slider and textField values */
    onChange: (value: number) => void;

    /** sets spacing around the slider*/
    spacingAround?: 1 | 2 | 3 | 4 | 5;

    /** props for textField */
    textFieldProps?: Partial<Omit<TextFieldProps, "onBlur" | "onChange" | "type" | "value">>;

    /** value of the slider */
    value: number;
};

const INPUT_STYLES = {
	width: 50,
	/** Removes the up & down dials */
	"& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
		display: "none",
		WebkitAppearance: "none",
		margin: 0,
	},
	"& input[type=number]": {
		MozAppearance: "textfield",
	},
};

const Slider: FunctionComponent<SliderProps> = (props) => {
	const { includeTextField, textFieldProps, min, max, step, value, spacingAround, onChange, ...otherProps } = props;
	const { inputProps, ...otherTextFieldProps } = textFieldProps || {};
	const invalidValue = value < min || value > max;

	const onBlur = () => {
		if (value < min) {
			onChange(min);
		} else if (value > max) {
			onChange(max);
		}
	};

	return (
		<GridRow
			grow
			growChild={0}
			spacing={4}
			spacingAround={spacingAround ? spacingAround : 1}
			sx={{
				/** Allows for the thumb hover state to overflow */
				"& .MuiGrid-root": {
					overflow: "initial",
				},
			}}
		>
			<MuiSlider
				max={max}
				min={min}
				onChange={(event: Event, value: number) => onChange(value)}
				step={step}
				value={value}
				{...otherProps}
			/>
			{includeTextField && (
				<TextField
					error={invalidValue}
					inputProps={{
						max,
						min,
						step,
						...inputProps,
					}}
					onBlur={onBlur}
					onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(Number(event.target.value))}
					sx={INPUT_STYLES}
					type="number"
					value={value}
					{...otherTextFieldProps}
				/>
			)}
		</GridRow>
	);
};

export default Slider;
